/**
 * Block D — D0 — Host's meeting-type list + create/edit/delete UI.
 *
 * Strictly host-side. No invitee-facing surface in D0 (that lands in
 * D2). Reuses the existing `PublicLayout`, sonner toasts and shadcn
 * dialog primitives.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Clock, MapPin, CalendarPlus } from "lucide-react";

const COLOR_KEYS = [
  "emerald", "sky", "violet", "indigo", "turquoise", "amber",
  "yellow", "orange", "rose", "pink", "red", "slate", "gray",
];
const COLOR_DOT = {
  emerald: "bg-emerald-500", sky: "bg-sky-500", violet: "bg-violet-500",
  indigo: "bg-indigo-500", turquoise: "bg-teal-500", amber: "bg-amber-500",
  yellow: "bg-yellow-400", orange: "bg-orange-500", rose: "bg-rose-500",
  pink: "bg-pink-500", red: "bg-red-500", slate: "bg-slate-500", gray: "bg-gray-400",
};

const EMPTY_FORM = {
  name: "",
  description: "",
  duration_min: 30,
  location_label: "",
  color_key: "emerald",
  is_published: false,
};

const MyMeetingTypes = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/calendar/meeting-types");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, t));
    } finally {
      setLoaded(true);
    }
  }, [t]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      name: it.name || "",
      description: it.description || "",
      duration_min: it.duration_min || 30,
      location_label: it.location_label || "",
      color_key: it.color_key || "emerald",
      is_published: !!it.is_published,
    });
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name?.trim() || "",
        description: form.description?.trim() || null,
        duration_min: Number(form.duration_min) || 0,
        location_label: form.location_label?.trim() || null,
        color_key: form.color_key,
        is_published: !!form.is_published,
      };
      if (editingId) {
        await api.put(`/calendar/meeting-types/${editingId}`, payload);
        toast.success(t("calendar.meetingTypes.toastUpdated"));
      } else {
        await api.post("/calendar/meeting-types", payload);
        toast.success(t("calendar.meetingTypes.toastCreated"));
      }
      setOpen(false);
      await reload();
    } catch (e) {
      toast.error(extractErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("calendar.meetingTypes.confirmDelete"))) return;
    try {
      await api.delete(`/calendar/meeting-types/${id}`);
      toast.success(t("calendar.meetingTypes.toastDeleted"));
      await reload();
    } catch (e) {
      toast.error(extractErrorMessage(e, t));
    }
  };

  const empty = useMemo(() => loaded && items.length === 0, [loaded, items]);

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="my-meeting-types-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate("/app/calendar")}
              data-testid="mt-back-btn"
              className="text-slate-500 hover:text-slate-700 -ms-2"
            >
              <ArrowLeft className="w-4 h-4 me-1 rtl:rotate-180" />
              {t("calendar.meetingTypes.back")}
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2 mt-1">
              <CalendarPlus className="w-6 h-6 text-emerald-600" />
              {t("calendar.meetingTypes.title")}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {t("calendar.meetingTypes.subtitle")}
            </p>
          </div>
          <Button onClick={openCreate} data-testid="mt-new-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 me-1" />
            {t("calendar.meetingTypes.newBtn")}
          </Button>
        </div>

        {!loaded && (
          <div className="text-sm text-slate-500" data-testid="mt-loading">
            {t("common.loading")}
          </div>
        )}

        {empty && (
          <Card data-testid="mt-empty-state" className="border-dashed">
            <CardContent className="py-12 text-center">
              <CalendarPlus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium mb-1">
                {t("calendar.meetingTypes.emptyTitle")}
              </p>
              <p className="text-sm text-slate-500 mb-4">
                {t("calendar.meetingTypes.emptyBody")}
              </p>
              <Button onClick={openCreate} data-testid="mt-empty-cta" variant="outline">
                <Plus className="w-4 h-4 me-1" />
                {t("calendar.meetingTypes.newBtn")}
              </Button>
            </CardContent>
          </Card>
        )}

        {loaded && items.length > 0 && (
          <div className="grid gap-3" data-testid="mt-list">
            {items.map((it) => (
              <Card key={it.id} data-testid={`mt-row-${it.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4 flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full ${COLOR_DOT[it.color_key] || COLOR_DOT.emerald}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-800 truncate">{it.name}</h3>
                      {it.is_published ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          {t("calendar.meetingTypes.statusPublished")}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">
                          {t("calendar.meetingTypes.statusDraft")}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {it.duration_min} {t("calendar.meetingTypes.minutes")}
                      </span>
                      {it.location_label && (
                        <span className="inline-flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3" />
                          {it.location_label}
                        </span>
                      )}
                    </div>
                    {it.description && (
                      <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{it.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm" variant="ghost" onClick={() => openEdit(it)}
                      data-testid={`mt-edit-${it.id}`}
                      aria-label={t("common.edit")}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm" variant="ghost" onClick={() => remove(it.id)}
                      data-testid={`mt-delete-${it.id}`}
                      aria-label={t("common.delete")}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="mt-form-dialog">
            <DialogHeader>
              <DialogTitle>
                {editingId
                  ? t("calendar.meetingTypes.editTitle")
                  : t("calendar.meetingTypes.createTitle")}
              </DialogTitle>
              <DialogDescription>
                {t("calendar.meetingTypes.formIntro")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">
                  {t("calendar.meetingTypes.fieldName")} *
                </label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={120}
                  data-testid="mt-field-name"
                  placeholder={t("calendar.meetingTypes.fieldNamePlaceholder")}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">
                  {t("calendar.meetingTypes.fieldDuration")} *
                </label>
                <Input
                  type="number" min={5} max={480}
                  value={form.duration_min}
                  onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                  data-testid="mt-field-duration"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {t("calendar.meetingTypes.fieldDurationHelp")}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">
                  {t("calendar.meetingTypes.fieldLocation")}
                </label>
                <Input
                  value={form.location_label}
                  onChange={(e) => setForm({ ...form, location_label: e.target.value })}
                  maxLength={200}
                  data-testid="mt-field-location"
                  placeholder={t("calendar.meetingTypes.fieldLocationPlaceholder")}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">
                  {t("calendar.meetingTypes.fieldDescription")}
                </label>
                <Textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={1000}
                  data-testid="mt-field-description"
                  placeholder={t("calendar.meetingTypes.fieldDescriptionPlaceholder")}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700 block mb-2">
                  {t("calendar.meetingTypes.fieldColor")}
                </label>
                <div className="flex flex-wrap gap-2" data-testid="mt-field-color-swatch">
                  {COLOR_KEYS.map((c) => (
                    <button
                      key={c} type="button"
                      onClick={() => setForm({ ...form, color_key: c })}
                      data-testid={`mt-color-${c}`}
                      className={`w-7 h-7 rounded-full ${COLOR_DOT[c]} ring-2 transition-all ${
                        form.color_key === c ? "ring-slate-800 ring-offset-2" : "ring-transparent"
                      }`}
                      aria-label={c}
                    />
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.is_published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                  data-testid="mt-field-published"
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600"
                />
                <span className="text-sm text-slate-700">
                  {t("calendar.meetingTypes.fieldPublished")}
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="mt-form-cancel">
                {t("common.cancel")}
              </Button>
              <Button
                onClick={submit} disabled={busy || !form.name?.trim()}
                data-testid="mt-form-submit"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {busy
                  ? t("common.saving")
                  : editingId
                    ? t("common.save")
                    : t("calendar.meetingTypes.createBtn")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PublicLayout>
  );
};

export default MyMeetingTypes;
